import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../api';
import { Search, RotateCcw, Shield, User, ChevronRight, Plus, Trash2, Copy, Users, Pencil, CheckCheck } from 'lucide-react';

const Permissions = ({ user, setUser }) => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState({});

  // Presets state
  const [presets, setPresets] = useState([]);
  const [presetDialog, setPresetDialog] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({ name: '', description: '', allowed_sections: [] });
  const [presetSections, setPresetSections] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);

  // Apply preset state
  const [applyDialog, setApplyDialog] = useState(false);
  const [applyPreset, setApplyPreset] = useState(null);
  const [applySearch, setApplySearch] = useState('');
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [applying, setApplying] = useState(false);

  // Fetch departments once
  useEffect(() => {
    api.get('/departments').then(res => {
      const map = {};
      (res.data || []).forEach(d => { map[d.id] = d.name; });
      setDepartments(map);
    }).catch(() => {});
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await api.get(`/permissions/employees${params}`);
      setEmployees(res.data);
    } catch {
      toast.error('Failed to load employees');
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchEmployees, 300);
    return () => clearTimeout(timer);
  }, [fetchEmployees]);

  const fetchPresets = async () => {
    try {
      const res = await api.get('/permissions/presets');
      setPresets(res.data);
    } catch {
      toast.error('Failed to load presets');
    }
  };

  useEffect(() => { fetchPresets(); }, []);

  const selectEmployee = async (emp) => {
    setSelectedEmp(emp);
    setLoading(true);
    try {
      const res = await api.get(`/permissions/employee/${emp.id}`);
      setSections(res.data.sections);
    } catch {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (path) => {
    setSections(prev => prev.map(s =>
      s.path === path ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const toggleAll = (enabled) => {
    setSections(prev => prev.map(s => ({ ...s, enabled })));
  };

  const savePermissions = async () => {
    if (!selectedEmp) return;
    setSaving(true);
    try {
      const allowed = sections.filter(s => s.enabled).map(s => s.path);
      await api.put(`/permissions/employee/${selectedEmp.id}`, { allowed_sections: allowed });
      toast.success(`Permissions saved for ${selectedEmp.name}`);
      fetchEmployees();
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!selectedEmp) return;
    try {
      await api.delete(`/permissions/employee/${selectedEmp.id}`);
      toast.success('Reset to role defaults');
      selectEmployee(selectedEmp);
      fetchEmployees();
    } catch {
      toast.error('Failed to reset');
    }
  };

  // ===== PRESET HANDLERS =====
  const openPresetDialog = (preset = null) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetForm({ name: preset.name, description: preset.description || '', allowed_sections: preset.allowed_sections });
    } else {
      setEditingPreset(null);
      setPresetForm({ name: '', description: '', allowed_sections: [] });
    }
    // Build section toggles for preset editor
    api.get('/permissions/sections').then(res => {
      const sects = res.data.map(s => ({
        ...s,
        enabled: preset ? preset.allowed_sections.includes(s.path) : false
      }));
      setPresetSections(sects);
      setPresetDialog(true);
    }).catch(() => toast.error('Failed to load sections'));
  };

  const togglePresetSection = (path) => {
    setPresetSections(prev => prev.map(s =>
      s.path === path ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const savePreset = async () => {
    if (!presetForm.name.trim()) { toast.error('Preset name required'); return; }
    const allowed = presetSections.filter(s => s.enabled).map(s => s.path);
    if (allowed.length === 0) { toast.error('Select at least one section'); return; }
    setSavingPreset(true);
    try {
      if (editingPreset) {
        await api.put(`/permissions/presets/${editingPreset.id}`, {
          name: presetForm.name, description: presetForm.description, allowed_sections: allowed
        });
        toast.success('Preset updated');
      } else {
        await api.post('/permissions/presets', {
          name: presetForm.name, description: presetForm.description, allowed_sections: allowed
        });
        toast.success('Preset created');
      }
      setPresetDialog(false);
      fetchPresets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/permissions/presets/${id}`);
      toast.success('Preset deleted');
      fetchPresets();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openApplyDialog = (preset) => {
    setApplyPreset(preset);
    setSelectedEmpIds([]);
    setApplySearch('');
    setApplyDialog(true);
  };

  const toggleEmpSelection = (empId) => {
    setSelectedEmpIds(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const selectAllVisible = () => {
    const filtered = getFilteredApplyEmployees();
    const allIds = filtered.map(e => e.id);
    const allSelected = allIds.every(id => selectedEmpIds.includes(id));
    if (allSelected) {
      setSelectedEmpIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedEmpIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const getFilteredApplyEmployees = () => {
    if (!applySearch) return employees;
    const q = applySearch.toLowerCase();
    return employees.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.employee_id.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    );
  };

  const handleApplyPreset = async () => {
    if (selectedEmpIds.length === 0) { toast.error('Select at least one employee'); return; }
    setApplying(true);
    try {
      const res = await api.post(`/permissions/presets/${applyPreset.id}/apply`, {
        employee_ids: selectedEmpIds
      });
      toast.success(`Preset applied to ${res.data.applied} employees`);
      setApplyDialog(false);
      fetchPresets();
      fetchEmployees();
      if (selectedEmp && selectedEmpIds.includes(selectedEmp.id)) {
        selectEmployee(selectedEmp);
      }
    } catch {
      toast.error('Failed to apply preset');
    } finally {
      setApplying(false);
    }
  };

  // Save current employee toggles as a new preset
  const saveAsPreset = () => {
    const allowed = sections.filter(s => s.enabled).map(s => s.path);
    setEditingPreset(null);
    setPresetForm({ name: '', description: `Based on ${selectedEmp?.name}'s permissions`, allowed_sections: allowed });
    setPresetSections(sections.map(s => ({ path: s.path, label: s.label, enabled: s.enabled })));
    setPresetDialog(true);
  };

  const roleBadgeColor = (role) => {
    const map = { admin: 'bg-red-100 text-red-700', hr_manager: 'bg-indigo-100 text-indigo-700', hr_executive: 'bg-blue-100 text-blue-700', accounts_manager: 'bg-emerald-100 text-emerald-700', ops_manager: 'bg-amber-100 text-amber-700', employee: 'bg-slate-100 text-slate-600' };
    return map[role] || map.employee;
  };

  const filteredApplyEmps = getFilteredApplyEmployees();

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="permissions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="permissions-title">Permissions</h1>
            <p className="text-sm text-slate-500 mt-1">Control which sections each employee can access</p>
          </div>
        </div>

        <Tabs defaultValue="employees" className="w-full">
          <TabsList>
            <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="presets" data-testid="tab-presets">Presets</TabsTrigger>
          </TabsList>

          {/* ===== EMPLOYEES TAB ===== */}
          <TabsContent value="employees" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Employee List */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Employees</CardTitle>
                  <div className="relative mt-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search by name, ID, email..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-9"
                      data-testid="employee-search"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0 max-h-[65vh] overflow-y-auto">
                  {employees.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-8">No employees found</p>
                  )}
                  {employees.map(emp => (
                    <button
                      key={emp.id}
                      onClick={() => selectEmployee(emp)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-center gap-3 ${selectedEmp?.id === emp.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''}`}
                      data-testid={`emp-row-${emp.employee_id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{emp.name}</p>
                        <p className="text-xs text-slate-400 truncate">{emp.employee_id} · {departments[emp.department_id] || ''}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge variant="secondary" className={`text-[10px] px-1.5 ${roleBadgeColor(emp.role)}`}>
                          {emp.role?.replace('_', ' ')}
                        </Badge>
                        {emp.has_custom_permissions && (
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" title="Custom permissions" />
                        )}
                      </div>
                      <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Permissions Panel */}
              <Card className="lg:col-span-2">
                {!selectedEmp ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Shield size={40} className="mb-3 text-slate-300" />
                    <p className="text-sm">Select an employee to manage permissions</p>
                  </div>
                ) : loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                  </div>
                ) : (
                  <>
                    <CardHeader className="pb-3 border-b">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <CardTitle className="text-lg" data-testid="selected-emp-name">{selectedEmp.name}</CardTitle>
                          <span className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                            {selectedEmp.employee_id} · <Badge variant="secondary" className={`text-[10px] px-1.5 ${roleBadgeColor(selectedEmp.role)}`}>{selectedEmp.role?.replace('_', ' ')}</Badge>
                          </span>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => toggleAll(true)} className="text-xs" data-testid="enable-all-btn">Enable All</Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleAll(false)} className="text-xs" data-testid="disable-all-btn">Disable All</Button>
                          <Button variant="outline" size="sm" onClick={saveAsPreset} className="text-xs gap-1" data-testid="save-as-preset-btn">
                            <Copy size={12} /> Save as Preset
                          </Button>
                          <Button variant="outline" size="sm" onClick={resetToDefaults} className="text-xs gap-1" data-testid="reset-defaults-btn">
                            <RotateCcw size={12} /> Reset
                          </Button>
                          <Button size="sm" onClick={savePermissions} disabled={saving} className="gap-1" data-testid="save-permissions-btn">
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4 max-h-[60vh] overflow-y-auto">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                        {sections.map(s => (
                          <div key={s.path} className="flex items-center justify-between py-2.5 border-b border-slate-50" data-testid={`section-toggle-${s.path.replace(/\//g, '-').replace(/^-/, '')}`}>
                            <div className="flex items-center gap-2">
                              <Label className={`text-sm cursor-pointer ${s.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                                {s.label}
                              </Label>
                              {s.is_role_default && (
                                <span className="text-[9px] text-slate-400 bg-slate-100 px-1 rounded">default</span>
                              )}
                            </div>
                            <Switch
                              checked={s.enabled}
                              onCheckedChange={() => toggleSection(s.path)}
                              data-testid={`toggle-${s.path.replace(/\//g, '-').replace(/^-/, '')}`}
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ===== PRESETS TAB ===== */}
          <TabsContent value="presets" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Create reusable permission templates and apply them to multiple employees at once</p>
                <Button size="sm" onClick={() => openPresetDialog()} className="gap-1.5" data-testid="create-preset-btn">
                  <Plus size={14} /> Create Preset
                </Button>
              </div>

              {presets.length === 0 ? (
                <Card>
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Shield size={36} className="mb-3 text-slate-300" />
                    <p className="text-sm font-medium">No presets yet</p>
                    <p className="text-xs mt-1">Create your first preset to quickly assign permissions</p>
                  </div>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {presets.map(preset => (
                    <Card key={preset.id} className="hover:shadow-md transition-shadow" data-testid={`preset-card-${preset.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base truncate" data-testid={`preset-name-${preset.id}`}>{preset.name}</CardTitle>
                            {preset.description && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{preset.description}</p>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex flex-wrap gap-1 mb-3 max-h-20 overflow-hidden">
                          {preset.allowed_sections.slice(0, 8).map(path => {
                            const sec = path === '/' ? 'Dashboard' : path.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                            return <Badge key={path} variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-600">{sec}</Badge>;
                          })}
                          {preset.allowed_sections.length > 8 && (
                            <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500">+{preset.allowed_sections.length - 8} more</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mb-3">{preset.allowed_sections.length} sections enabled</p>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => openApplyDialog(preset)} className="flex-1 gap-1" data-testid={`apply-preset-${preset.id}`}>
                            <Users size={13} /> Apply
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openPresetDialog(preset)} data-testid={`edit-preset-${preset.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deletePreset(preset.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-preset-${preset.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* ===== CREATE/EDIT PRESET DIALOG ===== */}
        <Dialog open={presetDialog} onOpenChange={setPresetDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPreset ? 'Edit Preset' : 'Create Preset'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Preset Name</Label>
                <Input
                  value={presetForm.name}
                  onChange={e => setPresetForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., HR Full Access, Accounts View Only"
                  data-testid="preset-name-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={presetForm.description}
                  onChange={e => setPresetForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this preset..."
                  rows={2}
                  data-testid="preset-desc-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Sections ({presetSections.filter(s => s.enabled).length} of {presetSections.length} enabled)</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setPresetSections(p => p.map(s => ({ ...s, enabled: true })))} data-testid="preset-enable-all">Enable All</Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setPresetSections(p => p.map(s => ({ ...s, enabled: false })))} data-testid="preset-disable-all">Disable All</Button>
                  </div>
                </div>
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto grid grid-cols-2 gap-x-6 gap-y-0.5">
                  {presetSections.map(s => (
                    <div key={s.path} className="flex items-center justify-between py-2 border-b border-slate-50">
                      <Label className={`text-sm cursor-pointer ${s.enabled ? 'text-slate-800' : 'text-slate-400'}`}>{s.label}</Label>
                      <Switch checked={s.enabled} onCheckedChange={() => togglePresetSection(s.path)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPresetDialog(false)}>Cancel</Button>
              <Button onClick={savePreset} disabled={savingPreset} data-testid="save-preset-btn">
                {savingPreset ? 'Saving...' : editingPreset ? 'Update Preset' : 'Create Preset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== APPLY PRESET DIALOG ===== */}
        <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCheck size={18} className="text-indigo-500" />
                Apply "{applyPreset?.name}"
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Select employees to apply this preset. This will override their current permissions.
              </p>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search employees..."
                  value={applySearch}
                  onChange={e => setApplySearch(e.target.value)}
                  className="pl-9"
                  data-testid="apply-search"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                <span>{selectedEmpIds.length} selected</span>
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={selectAllVisible} data-testid="select-all-apply">
                  {filteredApplyEmps.every(e => selectedEmpIds.includes(e.id)) ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {filteredApplyEmps.map(emp => (
                  <label
                    key={emp.id}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${selectedEmpIds.includes(emp.id) ? 'bg-indigo-50' : ''}`}
                    data-testid={`apply-emp-${emp.employee_id}`}
                  >
                    <Checkbox
                      checked={selectedEmpIds.includes(emp.id)}
                      onCheckedChange={() => toggleEmpSelection(emp.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{emp.name}</p>
                      <p className="text-xs text-slate-400">{emp.employee_id} · {departments[emp.department_id] || ''}</p>
                    </div>
                    <Badge variant="secondary" className={`text-[10px] px-1.5 ${roleBadgeColor(emp.role)}`}>
                      {emp.role?.replace('_', ' ')}
                    </Badge>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyDialog(false)}>Cancel</Button>
              <Button onClick={handleApplyPreset} disabled={applying || selectedEmpIds.length === 0} className="gap-1" data-testid="confirm-apply-btn">
                <CheckCheck size={14} /> {applying ? 'Applying...' : `Apply to ${selectedEmpIds.length} Employee${selectedEmpIds.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Permissions;
